import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { companionMoments, homeConcierge, type CompanionMoment } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import type { MemberCtx } from './MemberHome';
import { callFirstOf, careWorkerOf, firstPerson, givenName } from './memberData';
import { useMyDay } from './useMyDay';
import { Act, D, S } from './ui';

// V7 Aithora Home, the calm top of Today (Aithora ConsoleV7.dc.html T11909, care-flow.js
// AithoraCF.home): "I need a hand" and "Atlas", one Next thing with I did it / Not now,
// and the newest voice note from the family with Play it / Call. 60px+ targets, 19px+ text.
// Every value is real:
//   - Next: the member's own My day (GET /my-day); I did it records it (POST /my-day/events).
//     Not now only moves on for this visit: nothing is recorded, and it comes back next time.
//   - I need a hand: a care request to the care team (POST /home/:id/concierge).
//   - From your family: the newest family voice note (GET /family/:id/moments), played from its
//     stored file; Call is a member action (POST /athome/me/:id/actions {kind:'call'}).

const BIG = 'display:flex;align-items:center;justify-content:center;gap:10px;min-height:72px;padding:0 18px;border-radius:18px;font-size:21px;font-weight:800;text-decoration:none;text-align:center;box-sizing:border-box;cursor:pointer;font-family:inherit;line-height:1.2;';
const CARD = 'display:flex;flex-direction:column;gap:10px;padding:18px;border-radius:18px;background:#FFFFFF;border:2px solid rgba(34,39,46,.14);';

const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const isToday = (iso: string) => new Date(iso).toDateString() === new Date().toDateString();

export function HomeCalm({ c }: { c: MemberCtx }) {
  const navigate = useNavigate();
  const { client, session } = useSession();
  const day = useMyDay({ personId: c.personId, variant: 'member', readOnly: c.readOnly });
  // A family's voice notes are the member's own: read only by the member (the server refuses
  // Aithora's team), so a team member viewing the Home does not ask for them.
  const own = session?.user.role === 'MEMBER';
  const moments = useQuery((signal) => companionMoments(client, { params: { residentId: c.personId }, signal }), [client, session?.token, c.personId], { enabled: own });
  const [later, setLater] = useState<Record<string, true>>({});
  const [hand, setHand] = useState<'idle' | 'sent' | 'ok'>('idle');
  const [handNote, setHandNote] = useState<string | null>(null);
  const [heard, setHeard] = useState(false);
  const [called, setCalled] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  useEffect(() => () => audio.current?.pause(), []);

  const people = c.d.people.data;
  const helperFull = careWorkerOf(people);
  const helper = helperFull ? givenName(helperFull) : null;
  const left = day.items.filter((v) => !v.isDone && !later[v.id]);
  const next = left[0] ?? null;

  const notes = (moments.data?.moments ?? []).filter((m: CompanionMoment) => m.source === 'family' && m.mediaType === 'voice');
  const note = notes[0] ?? null;
  const fromName = note?.createdBy?.name ? givenName(note.createdBy.name) : null;
  const caller = note ? fromName : callFirstOf(people, c.d.me.data?.primaryContact ?? null);
  const callTarget = note ? note.createdBy?.name ?? null : firstPerson(people)?.name ?? null;

  const askHand = () => {
    if (c.readOnly) return;
    homeConcierge(client, { params: { residentId: c.personId }, body: { organizationId: c.orgId ?? undefined, kind: 'other', note: 'I need a hand' } })
      .then(() => {
        setHand('sent');
        setHandNote(`${helper ?? 'Your care team'} has been asked to come. Stay where you are.`);
      })
      .catch((e: Error) => setHandNote(`It did not go through: ${e.message}. If you are in danger, call 911.`));
  };
  const okNow = () => {
    if (c.readOnly) return;
    c.act({ kind: 'message', target: helperFull, note: 'I’m OK now. No need to come.' })
      .then(() => {
        setHand('ok');
        setHandNote(null);
      })
      .catch((e: Error) => setHandNote(`It did not go through: ${e.message}.`));
  };
  const play = async () => {
    if (!note?.fileUrl) return;
    try {
      const res = await client.raw(note.fileUrl);
      if (!res.ok) throw new Error(String(res.status));
      const url = URL.createObjectURL(await res.blob());
      audio.current?.pause();
      audio.current = new Audio(url);
      await audio.current.play();
      // Tell the sender it was heard (POST /family/:id/moments/:id/shown); a viewer only plays it.
      if (!c.readOnly) await client.request('POST', `/api/aithora/family/${c.personId}/moments/${note.id}/shown`, { body: {} });
      setHeard(true);
    } catch {
      c.setToast('The voice note could not be played right now.');
    }
  };
  const call = () => {
    if (c.readOnly) return;
    c.act({ kind: 'call', target: callTarget })
      .then((done) => setCalled(done || `Calling ${caller ?? 'your family'}…`))
      .catch((e: Error) => c.setToast(`Not sent: ${e.message}.`));
  };

  return (
    <D css="display:flex;flex-direction:column;gap:14px;margin:0 0 16px;min-width:0;" testId="home-calm">
      <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:12px;">
        <Act css={BIG + 'color:#FFFFFF;background:#22272E;border:0;'} onClick={askHand} disabled={c.readOnly || hand === 'sent'} reason={c.reason}>
          I need a hand
        </Act>
        <Act className="cf-ink" css={BIG + 'color:#FFFFFF;background:#264C7E;border:0;flex-direction:column;gap:2px;'} onClick={() => navigate('/me')}>
          <span>Atlas</span>
          <span style={{ fontSize: 19, fontWeight: 600, color: '#DCE6F3' }}>or say &ldquo;Hey Siri, talk to Atlas&rdquo;</span>
        </Act>
      </D>
      {handNote && (
        <D css="display:flex;align-items:center;gap:14px;flex-wrap:wrap;padding:14px 16px;border-radius:16px;background:rgba(31,107,82,.12);" role="status">
          <S css="flex:1 1 200px;min-width:0;font-size:21px;font-weight:800;color:#12503C;line-height:1.3;">{handNote}</S>
          {hand === 'sent' && (
            <Act css={BIG + 'color:#12503C;background:#FFFFFF;border:2px solid rgba(31,107,82,.5);min-height:60px;font-size:19px;'} onClick={okNow} disabled={c.readOnly} reason={c.reason}>
              I&rsquo;m OK now
            </Act>
          )}
        </D>
      )}
      <D css={CARD}>
        <S css="font-size:19px;font-weight:700;color:#3C454F;">Next</S>
        {next ? (
          <>
            <S css="font-size:30px;font-weight:800;letter-spacing:-.02em;line-height:1.2;color:#22272E;">{next.label}</S>
            <S css="font-size:20px;color:#3C454F;line-height:1.35;">{next.isWater ? next.waterText : next.time}</S>
            <D css="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:4px;">
              <Act className="cf-ink" css={BIG + 'color:#FFFFFF;background:#1F6B52;border:0;'} onClick={() => (next.isWater ? next.onGlass() : next.onDo())} disabled={c.readOnly} reason={c.reason}>
                I did it
              </Act>
              <Act css={BIG + 'color:#22272E;background:#FFFFFF;border:2px solid rgba(34,39,46,.3);'} onClick={() => setLater((p) => ({ ...p, [next.id]: true }))}>
                Not now
              </Act>
            </D>
          </>
        ) : (
          <S css="font-size:24px;font-weight:800;color:#1F6B52;line-height:1.3;">
            {day.loading ? '' : day.items.length ? 'All done for today. Well done!' : 'Nothing on your day yet.'}
          </S>
        )}
        {left.length > 1 && (
          <S css="font-size:19px;color:#3C454F;line-height:1.4;">{`Later: ${left
            .slice(1, 4)
            .map((v) => v.label)
            .join(' · ')}`}</S>
        )}
      </D>
      <D css={CARD}>
        <S css="font-size:19px;font-weight:700;color:#3C454F;">From your family</S>
        <S css="font-size:26px;font-weight:800;line-height:1.25;color:#22272E;">{note ? `${fromName ?? 'Your family'} sent you a voice note` : 'No voice notes yet'}</S>
        <S css="font-size:19px;color:#3C454F;line-height:1.4;">
          {note
            ? `${isToday(note.createdAt) ? 'Today' : new Date(note.createdAt).toLocaleDateString([], { weekday: 'long' })} at ${clock(note.createdAt)}${note.caption ? ` · “${note.caption}”` : ''}`
            : own ? 'When your family sends one, it shows up here.' : 'Voice notes from family are the member’s own and are not shown to Aithora.'}
        </S>
        <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;">
          {note && (
            <Act className="cf-ink" css={BIG + 'color:#FFFFFF;background:#264C7E;border:0;'} onClick={() => void play()} disabled={!note.fileUrl} title={note.fileUrl ? undefined : 'The recording is not stored yet'}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
              {heard ? 'Play again' : 'Play it'}
            </Act>
          )}
          <Act css={BIG + 'color:#264C7E;background:#FFFFFF;border:2px solid rgba(47,92,151,.45);'} onClick={call} disabled={c.readOnly || !callTarget} reason={c.reason}>
            {`Call ${caller ?? 'your family'}`}
          </Act>
        </D>
        {heard && !c.readOnly && <S css="font-size:19px;font-weight:700;color:#1F6B52;">{'✓ '}{`${fromName ?? 'Your family'} will see that you heard it.`}</S>}
        {called && <S css="font-size:19px;font-weight:700;color:#1F6B52;">{called}</S>}
      </D>
    </D>
  );
}
